import { useMessageScroller } from "@shadcn/react/message-scroller";
import { useLayoutEffect, useRef } from "react";
import { useChatPage } from "../-hooks/useChatPage";
import {
    composerInsetDurationVariable,
    readCssPixel,
    shouldPinChatToComposer,
} from "../-utils/composerLayoutHeight";
import { getChatScrollerViewport } from "../-utils/scrollToReplyTarget";

export function ScrollToLatestOnSend() {
    const { sendScrollGeneration } = useChatPage();
    const { scrollToEnd } = useMessageScroller();

    useLayoutEffect(
        function scrollToEndAfterSend() {
            if (sendScrollGeneration === 0) {
                return;
            }

            // Native dismisses the keyboard as the message appears. A smooth
            // scroll is still running when the list inset shrinks, so the
            // shrink no longer counts as pinned and the last collapse is skipped.
            scrollToEnd({
                behavior: window.isIosNativeWebView ? "auto" : "smooth",
            });
        },
        [sendScrollGeneration, scrollToEnd],
    );

    return null;
}

export function ScrollWithComposerInset() {
    const { composerHeight } = useChatPage();
    const { scrollToEnd } = useMessageScroller();
    const previousHeightRef = useRef(composerHeight);

    useLayoutEffect(
        function keepBottomStuckToComposer() {
            const previousHeight = previousHeightRef.current;
            previousHeightRef.current = composerHeight;
            const delta = composerHeight - previousHeight;
            const viewport = getChatScrollerViewport();

            if (!viewport) {
                if (Math.abs(delta) >= 0.5) {
                    scrollToEnd({ behavior: "auto" });
                }
                return;
            }

            const distanceToBottom =
                viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;

            if (
                !shouldPinChatToComposer({
                    distanceToBottom,
                    delta,
                })
            ) {
                return;
            }

            const duration = readCssPixel(
                getComputedStyle(document.documentElement).getPropertyValue(
                    composerInsetDurationVariable,
                ),
            );
            if (duration < 0.5) {
                scrollToEnd({ behavior: "auto" });
                return;
            }

            // Padding eases in CSS. scrollTop cannot, so follow the growing
            // layout without rendering React on each frame.
            const startedAt = performance.now();
            let frame = 0;
            const follow = (time: number) => {
                viewport.scrollTop = viewport.scrollHeight - viewport.clientHeight;
                if (time - startedAt < duration) {
                    frame = window.requestAnimationFrame(follow);
                }
            };
            frame = window.requestAnimationFrame(follow);
            return () => {
                window.cancelAnimationFrame(frame);
            };
        },
        [composerHeight, scrollToEnd],
    );

    return null;
}
